/**
 * Página administrativa para sobrescrever manualmente valores de Novas/Baixadas/Total
 * por mês no gráfico de Unidades (Novas x Baixadas).
 *
 * Útil quando a Claro faz operações manuais que não são capturadas automaticamente.
 */
import { useState, useEffect, useCallback } from 'react';
import { Building2, Pencil, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { getApiUrl } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface MonthOverride {
  novas?: number;
  baixadas?: number;
  total?: number;
}

interface OverridesData {
  overrides: Record<string, MonthOverride>;
  updatedAt: string | null;
  updatedBy: string | null;
}

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function formatMonthLabel(key: string): string {
  const [year, month] = key.split('-');
  const idx = parseInt(month, 10) - 1;
  return `${MONTH_LABELS[idx] || month}/${year}`;
}

export function AdminUnidadesOverridesPage() {
  const [data, setData] = useState<OverridesData>({ overrides: {}, updatedAt: null, updatedBy: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteMonth, setDeleteMonth] = useState<string | null>(null);

  // Form state
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [formNovas, setFormNovas] = useState('');
  const [formBaixadas, setFormBaixadas] = useState('');
  const [formTotal, setFormTotal] = useState('');

  const { toast } = useToast();

  const loadOverrides = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(getApiUrl('/claro/api/units/overrides'));
      const json = await res.json();
      if (json.status === 'success') {
        setData(json.result);
      }
    } catch {
      toast({ title: 'Erro ao carregar overrides', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  const handleOpenDialog = (monthKey?: string) => {
    if (monthKey) {
      const [year, month] = monthKey.split('-');
      setSelectedYear(year);
      setSelectedMonth(month);
      const ov = data.overrides[monthKey];
      setFormNovas(ov?.novas !== undefined ? String(ov.novas) : '');
      setFormBaixadas(ov?.baixadas !== undefined ? String(ov.baixadas) : '');
      setFormTotal(ov?.total !== undefined ? String(ov.total) : '');
    } else {
      setSelectedYear(String(new Date().getFullYear()));
      setSelectedMonth(String(new Date().getMonth() + 1).padStart(2, '0'));
      setFormNovas('');
      setFormBaixadas('');
      setFormTotal('');
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    const monthKey = `${selectedYear}-${selectedMonth}`;

    const novas = formNovas.trim() !== '' ? parseInt(formNovas, 10) : undefined;
    const baixadas = formBaixadas.trim() !== '' ? parseInt(formBaixadas, 10) : undefined;
    const total = formTotal.trim() !== '' ? parseInt(formTotal, 10) : undefined;

    if (novas === undefined && baixadas === undefined && total === undefined) {
      toast({ title: 'Preencha ao menos um campo', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(getApiUrl('/claro/api/units/overrides'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: monthKey, novas, baixadas, total }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setData(json.result);
        setIsDialogOpen(false);
        toast({ title: `Override para ${formatMonthLabel(monthKey)} salvo` });
      } else {
        toast({ title: json.message || 'Erro ao salvar', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Erro ao salvar override', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (month: string) => {
    try {
      const res = await fetch(getApiUrl(`/claro/api/units/overrides/${month}`), {
        method: 'DELETE',
      });
      const json = await res.json();
      if (json.status === 'success') {
        setData(json.result);
        toast({ title: `Override para ${formatMonthLabel(month)} removido` });
      }
    } catch {
      toast({ title: 'Erro ao remover override', variant: 'destructive' });
    } finally {
      setDeleteMonth(null);
    }
  };

  const sortedOverrides = Object.entries(data.overrides).sort(([a], [b]) => b.localeCompare(a));

  // Anos disponíveis para seleção (2025 até ano atual + 1)
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: currentYear - 2025 + 2 }, (_, i) => String(2025 + i));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Ajustes Manuais — Novas x Baixadas
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Sobrescrever manualmente os valores de Novas, Baixadas ou Total para meses específicos no gráfico de Unidades.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="h-4 w-4" />
          Novo Override
        </Button>
      </div>

      {/* Aviso */}
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <CardContent className="flex items-start gap-3 pt-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Atenção</p>
            <p>
              Os overrides são aplicados apenas no modo global (sem filtros de regional/UF).
              Valores preenchidos aqui sobrescrevem o cálculo automático para o mês correspondente.
              Deixe um campo vazio para manter o valor calculado automaticamente.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de overrides existentes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Overrides Ativos</CardTitle>
          <CardDescription>
            {sortedOverrides.length === 0
              ? 'Nenhum override configurado. Os valores são calculados automaticamente.'
              : `${sortedOverrides.length} override(s) ativo(s).`}
            {data.updatedAt && (
              <span className="ml-2 text-xs text-muted-foreground">
                Última atualização: {new Date(data.updatedAt).toLocaleString('pt-BR')}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedOverrides.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Mês</TableHead>
                  <TableHead className="text-right">Novas</TableHead>
                  <TableHead className="text-right">Baixadas</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-[100px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOverrides.map(([month, ov]) => (
                  <TableRow key={month}>
                    <TableCell className="font-medium">
                      <Badge variant="outline">{formatMonthLabel(month)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {ov.novas !== undefined ? (
                        <span className="text-emerald-600 font-medium">{ov.novas.toLocaleString('pt-BR')}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">auto</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {ov.baixadas !== undefined ? (
                        <span className="text-red-600 font-medium">{ov.baixadas.toLocaleString('pt-BR')}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">auto</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {ov.total !== undefined ? (
                        <span className="text-blue-600 font-medium">{ov.total.toLocaleString('pt-BR')}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">auto</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(month)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteMonth(month)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nenhum override configurado ainda.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de criação/edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Override</DialogTitle>
            <DialogDescription>
              Defina os valores manuais para o mês selecionado. Campos vazios mantêm o cálculo automático.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ano</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map(y => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mês</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_LABELS.map((label, idx) => (
                      <SelectItem key={idx} value={String(idx + 1).padStart(2, '0')}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="novas">Novas (unidades registradas no mês)</Label>
              <Input
                id="novas"
                type="number"
                min="0"
                placeholder="Deixe vazio para calcular automaticamente"
                value={formNovas}
                onChange={e => setFormNovas(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="baixadas">Baixadas (unidades arquivadas no mês)</Label>
              <Input
                id="baixadas"
                type="number"
                min="0"
                placeholder="Deixe vazio para calcular automaticamente"
                value={formBaixadas}
                onChange={e => setFormBaixadas(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="total">Total de unidades ao final do mês</Label>
              <Input
                id="total"
                type="number"
                min="0"
                placeholder="Deixe vazio para calcular automaticamente"
                value={formTotal}
                onChange={e => setFormTotal(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteMonth !== null} onOpenChange={() => setDeleteMonth(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o override para{' '}
              <strong>{deleteMonth ? formatMonthLabel(deleteMonth) : ''}</strong>?
              O valor voltará a ser calculado automaticamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMonth(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMonth && handleDelete(deleteMonth)}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
